import React, { useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import Navigation from './components/Navigation';
import Footer from './components/Footer';
import FloatingContactForm from './components/FloatingContactForm';
import LuxuryCursor from './components/LuxuryCursor';
import HomePage from './pages/HomePage';
import AboutPage from './pages/AboutPage';
import ServicesPage from './pages/ServicesPage';
import GalleryPage from './pages/GalleryPage';
import ContactPage from './pages/ContactPage';
import './index.css';

const PageEffects = () => {
  const location = useLocation();
  const transitionTimer = useRef(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });

    const root = document.documentElement;
    root.classList.add('page-entering');
    window.clearTimeout(transitionTimer.current);
    transitionTimer.current = window.setTimeout(() => root.classList.remove('page-entering'), 520);

    const sections = Array.from(document.querySelectorAll('main section'));
    const revealTargets = [];

    sections.forEach((section, sectionIndex) => {
      section.classList.add('reveal-section');
      revealTargets.push(section);

      const heading = section.querySelector('h1, h2');
      if (heading) heading.classList.add('reveal-heading');

      section.querySelectorAll('.grid > *, .space-y-24 > *, .gallery-item').forEach((item, index) => {
        item.classList.add('reveal-card');
        item.style.setProperty('--reveal-delay', `${Math.min(index * 90, 450)}ms`);
        revealTargets.push(item);
      });
    });

    const hero = sections[0];
    if (hero) {
      hero.classList.add('page-hero');
      const heroImage = hero.querySelector('img');
      if (heroImage) heroImage.classList.add('hero-image-zoom');
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -40px 0px' });

    revealTargets.forEach((target) => observer.observe(target));

    const magneticElements = Array.from(document.querySelectorAll('.btn-copper, .btn-outline-copper'));
    const magneticHandlers = magneticElements.map((element) => {
      const move = (event) => {
        const rect = element.getBoundingClientRect();
        const x = ((event.clientX - rect.left) / rect.width - 0.5) * 8;
        const y = ((event.clientY - rect.top) / rect.height - 0.5) * 8;
        element.style.setProperty('--mag-x', `${x}px`);
        element.style.setProperty('--mag-y', `${y}px`);
      };
      const leave = () => {
        element.style.setProperty('--mag-x', '0px');
        element.style.setProperty('--mag-y', '0px');
      };
      element.addEventListener('mousemove', move);
      element.addEventListener('mouseleave', leave);
      return { element, move, leave };
    });

    const updateProgress = () => {
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      const progress = scrollable > 0 ? window.scrollY / scrollable : 0;
      document.documentElement.style.setProperty('--scroll-progress', `${Math.min(Math.max(progress, 0), 1) * 100}%`);
    };
    updateProgress();
    window.addEventListener('scroll', updateProgress, { passive: true });

    return () => {
      observer.disconnect();
      magneticHandlers.forEach(({ element, move, leave }) => {
        element.removeEventListener('mousemove', move);
        element.removeEventListener('mouseleave', leave);
      });
      window.removeEventListener('scroll', updateProgress);
      window.clearTimeout(transitionTimer.current);
    };
  }, [location.pathname]);

  return <div className="page-transition-overlay" aria-hidden="true" />;
};

function App() {
  return (
    <Router>
      <div className="min-h-screen flex flex-col">
        <div className="scroll-progress" aria-hidden="true" />
        <Navigation />
        <LuxuryCursor />
        <main className="flex-grow">
          <PageEffects />
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/services" element={<ServicesPage />} />
            <Route path="/gallery" element={<GalleryPage />} />
            <Route path="/contact" element={<ContactPage />} />
          </Routes>
        </main>
        <Footer />
        <FloatingContactForm />
      </div>
    </Router>
  );
}

export default App;
